import React from 'react';
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {v4 as uuid} from 'uuid';
import {MAX_LIKERT_SCALE_SCALE_SIZE, MIN_LIKERT_SCALE_SCALE_SIZE} from '../../../../../constants';
import {IValidationErrorObject} from '../../../../../incase-domain/common/validationErrorResponse';
import Button from '../../../../../incase-domain/components/button/Button';
import {IElementLikertScale} from '../../../../../incase-domain/elements/question/likertScale';
import {MAX_LIKERT_SCALE_LENGTH} from '../../../../../incase-domain/validators/elements/constant';
import {scaleValidator} from '../../../../../incase-domain/validators/elements/likertScaleQuestion';
import {isDeleteButtonDisabled} from '../../../../../utils/utils';
import {generateErrorMessages, getErrorClasses} from '../../../../../utils/validation';
import SectionTitle from '../../../../elements/common/SectionTitle';
import DebouncedTextInput from '../../common/debounced/DebouncedTextInput';
import {changeLikertScaleItemText, removeLikertScaleItem, swapLikertScaleItemOrderHandler} from './utils/utils';

interface IHighlightedLikertScaleScaleComponentProps {
    highlightedElement: IElementLikertScale;
    errors: IValidationErrorObject | null;
    highlightedElementChangeHandler: {
        single: <T extends IElementLikertScale>(field: keyof T | string, value: unknown) => void;
        multiple: <T extends IElementLikertScale>(fields: (keyof T)[], values: unknown[]) => void;
    };
}

function LikertScaleScale(props: IHighlightedLikertScaleScaleComponentProps): JSX.Element {
    const {highlightedElement, errors, highlightedElementChangeHandler} = props;
    const {t} = useTranslation();

    function addLikertScaleQuestion(): void {
        if (highlightedElement.scale.length >= MAX_LIKERT_SCALE_SCALE_SIZE) {
            toast.error(
                `${t('presEditPage.editSidebar.elements.likertScaleQuestion.errorMessage.tooLargeScale', {
                    maxScale: MAX_LIKERT_SCALE_SCALE_SIZE,
                })}`
            );
            return;
        }
        const newLikertScaleQuestionText = `${highlightedElement.scale.length + 1}`;
        const newLikertScaleQuestion = [
            ...highlightedElement.scale,
            {
                id: uuid(),
                text: newLikertScaleQuestionText,
            },
        ];
        highlightedElementChangeHandler.single('scale', newLikertScaleQuestion);
    }

    return (
        <div className="l-group--form">
            <div className={`l-group__item ${getErrorClasses(errors, 'scale')}`}>
                <SectionTitle
                    label={t('presEditPage.editSidebar.elements.likertScaleQuestion.scale')}
                    leading={
                        <Button
                            className="c-sequenceQuestionElementOrder__shuffleButton"
                            variant="ghost"
                            size="2xs"
                            color="primary"
                            onClick={() => {
                                addLikertScaleQuestion();
                            }}
                            leading="ph:plus-bold"
                        >
                            {t('designSidepanel.new')}
                        </Button>
                    }
                />

                {generateErrorMessages(errors, `scale`)}

                <DragDropContext
                    onDragEnd={(e) => {
                        if (typeof e.destination?.index !== 'undefined')
                            swapLikertScaleItemOrderHandler(
                                'scale',
                                e.destination.index,
                                e.source.index,
                                highlightedElement,
                                highlightedElementChangeHandler
                            );
                    }}
                >
                    <Droppable droppableId={`liker-scale-${highlightedElement.id}`}>
                        {(provided) => {
                            return (
                                <div {...provided.droppableProps} ref={provided.innerRef}>
                                    {highlightedElement.scale.map((scale, index) => {
                                        return (
                                            <Draggable key={`${index}`} draggableId={`${index}-${scale.id}`} index={index}>
                                                {(provided) => {
                                                    return (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            key={`highlighted-element-likert-scale-scale-item-${index}`}
                                                        >
                                                            <div
                                                                className={`u-pt-1 ${getErrorClasses(errors, `scale.${index}.text`)}`}
                                                                key={scale.id}
                                                            >
                                                                <DebouncedTextInput
                                                                    className="c-sidepanel__input"
                                                                    value={scale.text}
                                                                    onChange={(value) =>
                                                                        changeLikertScaleItemText(
                                                                            'scale',
                                                                            index,
                                                                            value,
                                                                            highlightedElement,
                                                                            highlightedElementChangeHandler
                                                                        )
                                                                    }
                                                                    validator={(val) => {
                                                                        const validation = scaleValidator.shape.text.safeParse(val);
                                                                        return validation.success;
                                                                    }}
                                                                    maxLength={MAX_LIKERT_SCALE_LENGTH}
                                                                    leading="material-symbols:drag-indicator"
                                                                    trailing={
                                                                        <Button
                                                                            leading="solar:trash-bin-minimalistic-linear"
                                                                            variant="ghost"
                                                                            size="xs"
                                                                            color="error"
                                                                            square
                                                                            disableHover
                                                                            disabled={isDeleteButtonDisabled(
                                                                                highlightedElement.scale.length,
                                                                                MIN_LIKERT_SCALE_SCALE_SIZE + 1
                                                                            )}
                                                                            onClick={() =>
                                                                                removeLikertScaleItem(
                                                                                    'scale',
                                                                                    index,
                                                                                    highlightedElement,
                                                                                    highlightedElementChangeHandler
                                                                                )
                                                                            }
                                                                        />
                                                                    }
                                                                />
                                                            </div>
                                                        </div>
                                                    );
                                                }}
                                            </Draggable>
                                        );
                                    })}
                                    {provided.placeholder}
                                </div>
                            );
                        }}
                    </Droppable>
                </DragDropContext>
            </div>
        </div>
    );
}

export default LikertScaleScale;
